/**
 * The visibility of an attachment or a folder, as the panel edits it.
 *
 * The server knows one thing: the list of reparti the element is restricted to,
 * where an empty list is the whole structure. The form needs one thing more —
 * the operator can have chosen «Reparti specifici» and not picked a reparto yet,
 * which is neither of the two and must not be saved as the first — so the value
 * carries the choice (`ristretta`) beside the list (`reparti`), and only a
 * complete value is written.
 *
 * Reparti are compared by `nome`, never by `id`: every publication of a
 * gestione operativa or of a revision reclones the reparti with new ids, and a
 * restriction stored on an element keeps the id of the copy that was current
 * when it was saved. The server applies the same rule.
 */

export const INTERA_STRUTTURA = Object.freeze({
  ristretta: false,
  reparti: Object.freeze([]),
});

/** The value for a restriction the server returned; `[]`, `null` and `undefined` all mean the whole structure. */
export const visibilitaDa = (repartiVisibilita) => {
  const reparti = Array.isArray(repartiVisibilita) ? repartiVisibilita : [];
  return reparti.length > 0 ? { ristretta: true, reparti } : INTERA_STRUTTURA;
};

/** `false` only for «Reparti specifici» with no reparto picked, which cannot be saved. */
export const isVisibilitaCompleta = (visibilita) =>
  !visibilita.ristretta || visibilita.reparti.length > 0;

/**
 * What goes to the server: the ids of the chosen reparti, or an empty list for
 * the whole structure. An id is sent back as it was received, including for a
 * reparto no longer among the selectable ones — it is still a reparto of the
 * esercizio, and dropping it would widen the visibility by accident.
 */
export const idsVisibilita = (visibilita) =>
  visibilita.ristretta ? visibilita.reparti.map((reparto) => reparto.id) : [];

const nomiOrdinati = (reparti) =>
  [...new Set(reparti.map((reparto) => reparto.nome))].sort();

/** Same choice and same reparti by name, in any order. */
export const stessaVisibilita = (a, b) => {
  if (a.ristretta !== b.ristretta) return false;
  if (!a.ristretta) return true;
  const nomiA = nomiOrdinati(a.reparti);
  const nomiB = nomiOrdinati(b.reparti);
  return (
    nomiA.length === nomiB.length &&
    nomiA.every((nome, index) => nome === nomiB[index])
  );
};

/** Whether `nome` is one of the reparti the esercizio currently has. */
export const isRepartoPresente = (nome, repartiEsistenti) =>
  repartiEsistenti.some((reparto) => reparto.nome === nome);

/**
 * The chosen reparti that the destination folder hides from: reading inherits
 * the folder's restriction, so an element restricted to one of these would be
 * visible to nobody but the capo. `repartiAmmessi` is the folder's restriction,
 * or `null` when the folder sets no limit; `repartiEsistenti` is what the
 * esercizio currently has, or `null` while that is unknown.
 *
 * A reparto the esercizio no longer has is not counted: it is already flagged
 * as missing, and it hides the element from everyone on its own, whatever the
 * folder says. With the existing reparti unknown nothing can be told apart, so
 * nothing is counted either — a restriction read from the server is then sent
 * back as it was, which is the one write that cannot make things worse.
 */
export const nomiFuoriCartella = (
  visibilita,
  repartiAmmessi,
  repartiEsistenti,
) => {
  if (!visibilita.ristretta || !repartiAmmessi || !repartiEsistenti) return [];
  return [...new Set(visibilita.reparti.map((reparto) => reparto.nome))].filter(
    (nome) =>
      isRepartoPresente(nome, repartiEsistenti) &&
      !isRepartoPresente(nome, repartiAmmessi),
  );
};

/** `false` when a chosen reparto does not see the destination folder; such a value is not saved. */
export const isVisibilitaAmmessa = (
  visibilita,
  repartiAmmessi,
  repartiEsistenti,
) =>
  nomiFuoriCartella(visibilita, repartiAmmessi, repartiEsistenti).length === 0;

/**
 * The restriction a folder passes down to what is created inside it: its own
 * when it has one, otherwise the nearest restricted ancestor's. `catena` runs
 * from the root folder to the one the element lands in, each entry carrying its
 * own `repartiVisibilita`; an empty result is the whole structure.
 *
 * Reading, the server inherits the same way — a document is visible only where
 * its folder and its folder's parent are — so this is the default that keeps the
 * pill on the new element in line with what the responsabile will actually see.
 */
export const restrizioneEreditata = (catena) => {
  for (let index = catena.length - 1; index >= 0; index -= 1) {
    const propria = catena[index]?.repartiVisibilita;
    if (Array.isArray(propria) && propria.length > 0) return propria;
  }
  return [];
};
